import React, { useEffect, useRef, useState } from 'react';
import { COLORS, METRIC_CARDS, apexFontFamily } from './shared/chartTokens';
import './KeyMetricCard.css';

// ─────────────────────────────────────────────────────────────
// KeyMetricCard – Single animated metric card
// Props:
//   value:       number | string
//   label:       string
//   icon:        emoji
//   gradient:    CSS gradient string
//   accentColor: string
//   suffix:      string (e.g. '%')
// ─────────────────────────────────────────────────────────────

const ANIM_DURATION = 1200; // ms

const useCountUp = (target, duration = ANIM_DURATION) => {
  const [count, setCount] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const numTarget = parseFloat(String(target).replace(/[^0-9.]/g, '')) || 0;
    if (numTarget === 0) { setCount(0); return; }

    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * numTarget * 100) / 100);
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return count;
};

const SingleMetricCard = ({ value, label, icon, gradient, accentColor, suffix = '' }) => {
  const isPercent = suffix === '%' || String(value).includes('%');
  const rawNum = parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
  const animated = useCountUp(rawNum);

  const displayValue = isPercent
    ? `${Number(animated).toFixed(1)}%`
    : Math.round(animated).toLocaleString();

  return (
    <div className="metric-card" style={{ borderTop: `4px solid ${accentColor}` }}>
      {/* Background blobs */}
      <div className="metric-card-bg" style={{ background: gradient }} />
      <div className="metric-card-glow" style={{ background: gradient }} />

      {/* Icon */}
      <div className="metric-icon-bubble" style={{ background: `${accentColor}18`, border: `1.5px solid ${accentColor}33` }}>
        {icon}
      </div>

      {/* Value */}
      <div className="metric-value" style={{ color: accentColor }}>
        {displayValue}
      </div>

      {/* Label */}
      <div className="metric-label" style={{ color: COLORS.textMuted }}>
        {label}
      </div>
    </div>
  );
};

const SkeletonMetricCard = ({ accentColor }) => (
  <div className="metric-card metric-card--skeleton" style={{ borderTop: `4px solid ${accentColor}` }}>
    <div className="metric-skeleton-block" style={{ width: 48, height: 48, borderRadius: 12, marginBottom: 16 }} />
    <div className="metric-skeleton-block" style={{ width: '60%', height: 32, marginBottom: 10 }} />
    <div className="metric-skeleton-block" style={{ width: '80%', height: 13 }} />
  </div>
);

// ─────────────────────────────────────────────────────────────
// KeyMetricCard – Grid of 4 metric cards
// Props:
//   metrics: { totalDataPoints, overallCompletion, activeContributors, pendingReviews }
//   loading: bool – renders skeleton cards until the real metrics arrive
// ─────────────────────────────────────────────────────────────
const KeyMetricCard = ({ metrics = {}, loading = false }) => (
  <div style={{
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '24px',
  }}>
      {METRIC_CARDS.map((cfg) => (
        loading ? (
          <SkeletonMetricCard key={cfg.key} accentColor={cfg.accentColor} />
        ) : (
          <SingleMetricCard
            key={cfg.key}
            value={metrics[cfg.key] ?? 0}
            label={cfg.label}
            icon={cfg.icon}
            gradient={cfg.gradient}
            accentColor={cfg.accentColor}
            suffix={cfg.suffix || ''}
          />
        )
      ))}
  </div>
);

export default KeyMetricCard;
